import { useOnAppForeground } from "@common/hooks/useOnAppForeground"
import { useInterval } from "@common/utils/useInterval"
import NetInfo from "@react-native-community/netinfo"
import moment from "moment"
import { useRef } from "react"

export const LIVE_ACCESS_AWAY_MS = 5 * 60 * 1000
const DAY_CHECK_MS = 60 * 1000

// A student's GO opens when their next live is today, so data loaded the day
// before keeps it grey. Calls `refresh` when the app comes back after a while
// in the background, and when the day turns while it stays in front (a web
// tab left open, a laptop waking up) — not on mount, where the screen loads
// anyway. `refresh` resolves true once it has asked for the data, false when
// it skipped (in a live): a skipped refresh is tried again, and so is one the
// network kept from starting.
export function useLiveAccessRefresh(refresh, { enabled }) {
    const loadedAtRef = useRef(moment())
    const inFlightRef = useRef(false)

    const refreshNow = async () => {
        if (!enabled || inFlightRef.current) return
        inFlightRef.current = true
        try {
            // apiRequest reads an unknown network (null) as offline and greets
            // the returning student with a network dialog for a reload they
            // never asked for.
            if (!(await NetInfo.fetch()).isConnected) return
            if (await refresh()) loadedAtRef.current = moment()
        } finally {
            inFlightRef.current = false
        }
    }
    const loadedToday = () => loadedAtRef.current.isSame(moment(), "day")

    useOnAppForeground((msAway) => {
        if (msAway >= LIVE_ACCESS_AWAY_MS || !loadedToday()) refreshNow()
    })
    useInterval(
        () => {
            if (!loadedToday()) refreshNow()
        },
        enabled ? DAY_CHECK_MS : null
    )
}
